import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserProfile {
  id: string;
  avatar_url: string | null;
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

export const useUserProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setProfile(null);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      if (error) throw error;
      // If trigger missed (rare), create row on demand
      if (!data) {
        const { data: created } = await supabase
          .from('user_profiles')
          .insert({ id: user.id })
          .select('*')
          .maybeSingle();
        setProfile(created as UserProfile | null);
      } else {
        setProfile(data as UserProfile);
      }
    } catch (err) {
      console.error('Failed to load user profile:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateProfile = useCallback(
    async (updates: Partial<Pick<UserProfile, 'avatar_url' | 'display_name'>>) => {
      if (!user) return { error: new Error('not_authenticated') };
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select('*')
        .maybeSingle();
      if (!error && data) setProfile(data as UserProfile);
      return { error, data };
    },
    [user]
  );

  return { profile, loading, refresh, updateProfile };
};
